import { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import {
  Brain,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Lightbulb,
  Target,
  Activity,
  Zap,
  Clock,
  ChevronRight,
  Sparkles,
  MessageSquare,
  BarChart3,
  Shield
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLLM } from '@/hooks/useLLM';

interface ShotAnalysis {
  id: string;
  timestamp: string;
  shotId: string;
  overallQuality: number;
  qualityBreakdown: {
    technique: number;
    power: number;
    timing: number;
    placement: number;
  };
  techniqueFeedback: {
    strengths: string[];
    improvements: string[];
    keyFocus: string;
  };
  coachingSuggestions: Array<{
    priority: 'high' | 'medium' | 'low';
    category: 'technique' | 'power' | 'timing' | 'footwork' | 'mental';
    suggestion: string;
    drills?: string[];
  }>;
  patterns?: {
    identified: string[];
    trend?: 'improving' | 'stable' | 'declining';
    consistency?: number;
  };
  confidence: number;
  processingTime: number;
  provider: string;
}

interface AICoachingPanelProps {
  sessionId: string;
  playerId: string;
  playerName: string;
  latestShot?: any;
  recentAnalyses?: ShotAnalysis[];
  onInterventionRequest?: (urgency: 'low' | 'medium' | 'high') => void;
}

export function AICoachingPanel({
  sessionId,
  playerId,
  playerName,
  latestShot,
  recentAnalyses: _recentAnalyses = [],
  onInterventionRequest
}: AICoachingPanelProps) {
  const {
    isConnected,
    isAnalyzing,
    latestAnalysis,
    latestIntervention,
    analyzeShot,
    generateIntervention
  } = useLLM({
    autoConnect: true
  });

  // Analyze latest shot when it changes
  useEffect(() => {
    if (latestShot && isConnected) {
      const shotData = {
        ...latestShot,
        id: latestShot.id || `shot-${Date.now()}`,
        playerId,
        sessionId,
        timestamp: new Date().toISOString(),
        // Add required fields with defaults if not present
        shotType: latestShot.shotType || 'drive',
        ballSpeed: latestShot.ballSpeed || 30,
        ballTrajectory: latestShot.ballTrajectory || { angle: 15, spin: 100, direction: 0 },
        batSpeed: latestShot.batSpeed || 25,
        impactLocation: latestShot.impactLocation || { x: 0, y: 0 },
        batAngle: latestShot.batAngle || 45,
        distance: latestShot.distance || 50,
        height: latestShot.height || 10,
        boundaryCleared: latestShot.boundaryCleared || false,
        runs: latestShot.runs || 2,
        timing: latestShot.timing || 75,
        power: latestShot.power || 70,
        control: latestShot.control || 80,
        footwork: latestShot.footwork || 75
      };
      
      analyzeShot(shotData, {
        playerProfile: {
          id: playerId,
          name: playerName,
          skillLevel: 'intermediate'
        },
        sessionGoals: ['Improve timing', 'Work on footwork']
      });
    }
  }, [latestShot, isConnected, analyzeShot, playerId, playerName, sessionId]);

  const requestIntervention = async (urgency: 'low' | 'medium' | 'high') => {
    if (!isConnected || !latestShot) return;

    await generateIntervention(
      latestShot,
      [], // Previous shots not available in analysis format - pass empty array
      urgency
    );
    
    if (onInterventionRequest) {
      onInterventionRequest(urgency);
    }
  };

  const getQualityColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'outline';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'technique': return Target;
      case 'power': return Zap;
      case 'timing': return Clock;
      case 'footwork': return Activity;
      case 'mental': return Brain;
      default: return Lightbulb;
    }
  };

  return (
    <div className="space-y-4">
      {/* Connection Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-primary" />
          <h3 className="font-semibold">AI Coaching Intelligence</h3>
        </div>
        <Badge variant={isConnected ? 'default' : 'destructive'}>
          {isConnected ? 'Connected' : 'Disconnected'}
        </Badge>
      </div>

      {/* Real-time Analysis */}
      {isAnalyzing && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 animate-pulse text-primary" />
              <span className="text-sm text-muted-foreground">Analyzing shot...</span>
              <Progress value={66} className="flex-1" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Latest Analysis */}
      {latestAnalysis && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Shot Analysis</span>
              <Badge className={cn(getQualityColor(latestAnalysis.overallQuality))}>
                {latestAnalysis.overallQuality}/100
              </Badge>
            </CardTitle>
            <CardDescription>
              Processed in {latestAnalysis.processingTime}ms • {latestAnalysis.confidence}% confidence
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Quality Breakdown */}
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(latestAnalysis.qualityBreakdown).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                  <span className="text-sm capitalize">{key}</span>
                  <span className={cn('font-semibold', getQualityColor(value))}>
                    {value}
                  </span>
                </div>
              ))}
            </div>

            <Separator />

            {/* Technique Feedback */}
            <div className="space-y-3">
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Shield className="h-4 w-4 text-green-600" />
                  Strengths
                </h4>
                <ul className="space-y-1">
                  {latestAnalysis.techniqueFeedback.strengths.map((strength, i) => (
                    <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                      <ChevronRight className="h-3 w-3 mt-0.5 text-green-600" />
                      {strength}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  Areas for Improvement
                </h4>
                <ul className="space-y-1">
                  {latestAnalysis.techniqueFeedback.improvements.map((improvement, i) => (
                    <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                      <ChevronRight className="h-3 w-3 mt-0.5 text-yellow-600" />
                      {improvement}
                    </li>
                  ))}
                </ul>
              </div>

              <Alert>
                <Lightbulb className="h-4 w-4" />
                <AlertDescription>
                  <strong>Key Focus:</strong> {latestAnalysis.techniqueFeedback.keyFocus}
                </AlertDescription>
              </Alert>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Coaching Suggestions */}
      {latestAnalysis?.coachingSuggestions && latestAnalysis.coachingSuggestions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Coaching Suggestions</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[200px]">
              <div className="space-y-3">
                {latestAnalysis.coachingSuggestions.map((suggestion, i) => {
                  const Icon = getCategoryIcon(suggestion.category);
                  return (
                    <div key={i} className="p-3 border rounded-lg space-y-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-primary" />
                          <Badge variant={getPriorityBadgeVariant(suggestion.priority)}>
                            {suggestion.priority}
                          </Badge>
                          <Badge variant="outline">{suggestion.category}</Badge>
                        </div>
                      </div>
                      <p className="text-sm">{suggestion.suggestion}</p>
                      {suggestion.drills && suggestion.drills.length > 0 && (
                        <div className="text-xs text-muted-foreground">
                          <strong>Drills:</strong> {suggestion.drills.join(', ')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Pattern Recognition */}
      {latestAnalysis?.patterns && latestAnalysis.patterns.identified.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Pattern Recognition
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm">Trend</span>
              <Badge variant={
                latestAnalysis.patterns.trend === 'improving' ? 'default' :
                latestAnalysis.patterns.trend === 'declining' ? 'destructive' : 'secondary'
              }>
                {latestAnalysis.patterns.trend === 'improving' && <TrendingUp className="h-3 w-3 mr-1" />}
                {latestAnalysis.patterns.trend === 'declining' && <TrendingDown className="h-3 w-3 mr-1" />}
                {latestAnalysis.patterns.trend}
              </Badge>
            </div>
            {latestAnalysis.patterns.consistency && (
              <div className="flex items-center justify-between">
                <span className="text-sm">Consistency</span>
                <span className="font-semibold">{latestAnalysis.patterns.consistency}%</span>
              </div>
            )}
            <div className="space-y-1">
              {latestAnalysis.patterns.identified.map((pattern, i) => (
                <div key={i} className="text-sm text-muted-foreground">
                  • {pattern}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Interventions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Quick Intervention
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {latestIntervention && (
            <Alert className="mb-3">
              <AlertDescription>{latestIntervention}</AlertDescription>
            </Alert>
          )}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => requestIntervention('low')}
              disabled={!isConnected}
            >
              Encouragement
            </Button>
            <Button
              size="sm"
              variant="default"
              onClick={() => requestIntervention('medium')}
              disabled={!isConnected}
            >
              Technical Tip
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => requestIntervention('high')}
              disabled={!isConnected}
            >
              Urgent Correction
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}